// src/hooks/useS3upload.ts - Updated to work with complex S3 upload service including draft folder workflow
import { useState, useCallback } from 'react';
import { s3UploadService } from '../services/s3Upload.service';
import { 
  UploadProgress, 
  UploadResult, 
  UseS3UploadOptions 
} from '../types/upload.types';
import { ImageFile, Scene } from '../types/core.types';
import { ERROR_MESSAGES, PROGRESS_STAGES } from '../constants/upload.constants';
import { useUploadCtx } from '../contexts/UploadContext';
import { useAuth } from '../contexts/AuthContext';

interface UseS3UploadReturn {
  progress: UploadProgress;
  isUploading: boolean;
  startUpload: (images: ImageFile[], scene: Scene) => Promise<void>;
  cancelUpload: () => void;
  resetUpload: () => void;
}

export const useS3Upload = (options: UseS3UploadOptions = {}): UseS3UploadReturn => {
  const [progress, setProgress] = useState<UploadProgress>({
    uploadId: '',
    userId: '',
    folderName: '',
    totalFiles: 0,
    uploadedFiles: 0,
    currentFile: '',
    overallProgress: 0,
    uploadPercent: 0,
    blurPercent: 0,
    blurDone: false,
    status: 'idle',
    sessionUUID: ''
  });

  const [isUploading, setIsUploading] = useState(false);

  const { sequenceUuid, mintNewSequenceUuid } = useUploadCtx();
  const { user } = useAuth();

  const updateProgress = useCallback((uploadedCount: number, currentFile: string, stage: string = 'uploading') => {
    setProgress(prev => {
      let overallProgress = prev.overallProgress;
      let status: typeof prev.status = prev.status;

      if (stage === 'blur-detection') {
        // uploadedCount carries percent here
        return {
          ...prev,
          blurPercent: uploadedCount,
          blurDone: uploadedCount >= 100,
        };
      }

      if (stage === 'uploading') {
        const uploadPercent = Math.round((uploadedCount / prev.totalFiles) * 100);
        overallProgress = Math.round((uploadPercent / 100) * PROGRESS_STAGES.UPLOADING_FILES);

        return {
          ...prev,
          uploadedFiles: uploadedCount,
          currentFile,
          uploadPercent,
          overallProgress,
          status: 'uploading',
        };
      }

      if (stage === 'metadata') {
        overallProgress = PROGRESS_STAGES.UPLOADING_METADATA;
        return { ...prev, overallProgress };
      }

      if (stage === 'renaming') {
        overallProgress = PROGRESS_STAGES.RENAMING_FOLDER;
        return { ...prev, overallProgress };
      }

      if (stage === 'completed') {
        return { ...prev, overallProgress: 100, status: 'completed' };
      }

      return prev;
    });
  }, []);

  const startUpload = useCallback(async (images: ImageFile[], scene: Scene) => {
    if (!user?.uid) {
      throw new Error('User must be authenticated to upload files');
    }

    if (!scene) {
      throw new Error(ERROR_MESSAGES.NO_SCENE);
    }

    if (!images || images.length === 0) {
      throw new Error(ERROR_MESSAGES.NO_IMAGES);
    }

    try {
      setIsUploading(true);
      
      // Ensure we have a sequence UUID minted *before* any folder is generated
      const ensuredUuid = sequenceUuid || mintNewSequenceUuid();

      // Generate folder structure while reusing the pre-minted UUID
      const folderStructure = s3UploadService.generateFolderStructure(user.uid, ensuredUuid);
      
      // Initialize progress
      setProgress({
        uploadId: '',
        userId: folderStructure.userId,
        folderName: folderStructure.folderName, // Initially draft folder name
        totalFiles: images.length,
        uploadedFiles: 0,
        currentFile: '',
        overallProgress: 0,
        blurPercent: 0,
        blurDone: false,
        status: 'getting-credentials',
        sessionUUID: folderStructure.sessionUUID
      });

      // Get S3 credentials
      console.log('Getting S3 credentials...');
      const credentials = await s3UploadService.getCredentials(
        folderStructure.fullPath, 
        images.length
      );

      // Update progress with upload ID
      setProgress(prev => ({
        ...prev,
        uploadId: credentials.uploadId,
        status: 'uploading'
      }));

      // Initialize S3 client with scene data and progress tracking
      s3UploadService.initializeS3Client(credentials, folderStructure, scene, updateProgress);

      // Set up abort controller for cancellation
      const abortController = new AbortController();
      s3UploadService.setAbortController(abortController);

      console.log(`Starting upload of ${images.length} files to ${folderStructure.fullPath} (draft folder)...`);
      
      // Upload files with proper sequencing, metadata, and draft folder workflow
      const result: UploadResult = await s3UploadService.uploadFiles(
        images,
        credentials,
        folderStructure,
        scene,
        updateProgress,
        options.validationResults
      );

      // Update final progress with final folder name
      setProgress(prev => ({
        ...prev,
        status: 'completed',
        overallProgress: 100,
        currentFile: '',
        folderName: result.folderName // Use final folder name from result
      }));

      console.log('Upload completed successfully:', result);

      // Call completion callback if provided
      options.callbacks?.onComplete?.(result);

    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown upload error';
      console.error('Upload failed:', errorMessage);

      // Distinguish between a user-initiated cancellation and an actual error
      if (errorMessage === ERROR_MESSAGES.UPLOAD_CANCELLED) {
        // Preserve the cancelled state
        setProgress(prev => ({
          ...prev,
          status: 'cancelled',
          error: undefined,
        }));

        // Notify via optional callback
        options.callbacks?.onCancel?.();
      } else {
        setProgress(prev => ({
          ...prev,
          status: 'error',
          error: errorMessage,
        }));

        // Call error callback if provided
        options.callbacks?.onError?.(errorMessage);
      }
      
      throw error;
    } finally {
      setIsUploading(false);
      s3UploadService.cleanup();
    }
  }, [options.validationResults, options.callbacks, updateProgress, sequenceUuid, mintNewSequenceUuid]);

  const cancelUpload = useCallback(() => {
    console.log('Cancelling upload...');
    s3UploadService.cancelUpload();
    
    setProgress(prev => ({
      ...prev,
      status: 'cancelled'
    }));
    
    setIsUploading(false);
    
    // Call cancel callback if provided
    options.callbacks?.onCancel?.();
  }, [options.callbacks]);

  const resetUpload = useCallback(() => {
    setProgress({
      uploadId: '',
      userId: '',
      folderName: '',
      totalFiles: 0,
      uploadedFiles: 0,
      currentFile: '',
      overallProgress: 0,
      status: 'idle',
      sessionUUID: ''
    });
    setIsUploading(false);
    s3UploadService.cleanup();
  }, []);

  return {
    progress,
    isUploading,
    startUpload,
    cancelUpload,
    resetUpload
  };
};